import type React from 'react';
import { z } from 'zod';

export type ViewProps<T> = {
  data: T;
  setData: (data: T) => void;
};

export type ViewComponent<T> = React.FC<ViewProps<T>>;

export type RegistryEntry<S extends z.ZodTypeAny> = {
  schema: S;
  View: ViewComponent<z.infer<S>>;
};

export type AnyRegistryEntry = {
  schema: z.ZodTypeAny;
  View: ViewComponent<any>;
};

export type RegistryShape = Record<string, Record<number, AnyRegistryEntry>>;

export function sameForVersions<
  S extends z.ZodTypeAny,
  V extends readonly number[],
>(
  schema: S,
  View: ViewComponent<z.infer<S>>,
  versions: V
): Record<V[number], RegistryEntry<S>> {
  const entries = versions.map(v => [v, { schema, View }] as const);
  return Object.fromEntries(entries) as unknown as Record<
    V[number],
    RegistryEntry<S>
  >;
}

export type KindOf<R> = keyof R;

type VersionKey<R, K extends keyof R> = keyof R[K];
type ToNumber<T> = T extends `${infer N extends number}` ? N : never;

export type VersionOf<R, K extends keyof R> =
  | Extract<VersionKey<R, K>, number>
  | ToNumber<Extract<VersionKey<R, K>, string>>;

type NormalizeVersionKey<
  R,
  K extends keyof R,
  V extends VersionOf<R, K>,
> = V extends keyof R[K] ? V : `${V}` extends keyof R[K] ? `${V}` : never;

type SchemaFor<
  R,
  K extends keyof R,
  V extends VersionOf<R, K>,
> = R[K][NormalizeVersionKey<R, K, V>] extends {
  schema: infer S extends z.ZodTypeAny;
}
  ? S
  : never;

export type DataFor<R, K extends keyof R, V extends VersionOf<R, K>> = z.infer<
  SchemaFor<R, K, V>
>;

export type AnyDataFor<R> = {
  [K in keyof R]: {
    [V in VersionOf<R, K>]: DataFor<R, K, V>;
  }[VersionOf<R, K>];
}[keyof R];

export function deriveSchemaMap<R extends RegistryShape>(registry: R) {
  const result: any = {};
  for (const kind in registry) {
    result[kind] = {};
    for (const version in registry[kind]) {
      result[kind][version] = registry[kind][version as any].schema;
    }
  }
  return result as {
    [K in keyof R]: {
      [V in keyof R[K]]: R[K][V] extends { schema: infer S } ? S : never;
    };
  };
}
